import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { Check, Users } from 'lucide-react';
import { clientBaseBandOf, clientBaseBands } from '../clientPresentation';
import type { IntlLike } from '../clientPresentation';

export type ClientBasePopupProps = {
  open: boolean;
  onClose: () => void;
  /** The adviser's own band, so the explainer can say where they actually are. */
  level?: number | null;
  intl: IntlLike;
};

/**
 * The C.P.I. explainer, opened from the client-base indicator in the tab row.
 *
 * V1's version (cpiDetailPopup.js, still used by the V1 page) is six paragraphs
 * and a legend built out of `border-left: 20px solid`. It has one bigger problem
 * than its looks: it explains what the indicator means without ever saying what
 * THIS adviser's indicator currently says, so the adviser has to close it and go
 * back to reading a colour. This one leads with their own band and then explains
 * it. Every string is V1's, by id — the wording is not what needed changing.
 */
const ClientBasePopup = ({ open, onClose, level, intl }: ClientBasePopupProps) => {
  const message = (id: string) => intl.formatMessage({ id });
  const current = clientBaseBandOf(level, intl);
  const bands = clientBaseBands(intl);

  return (
    <Dialog open={open} onClose={onClose} fullWidth aria-labelledby="clientv2-cpi-title">
      <DialogTitle id="clientv2-cpi-title">{message('Client_Potential_Index')}</DialogTitle>
      <DialogContent className="clientv2-cpi-dialog">
        {/* The answer to 「what is my bar telling me?」, before any explanation. */}
        <div
          className="clientv2-cpi-hero"
          data-testid="cpi-hero"
          style={{ backgroundColor: current.tint, borderColor: current.color }}
        >
          <span className="clientv2-cpi-hero-icon" style={{ backgroundColor: current.color }} aria-hidden="true">
            <Users size={16} />
          </span>
          <div>
            <div className="clientv2-cpi-hero-label">{message('clientv2-client-base')}</div>
            <strong className="clientv2-cpi-hero-band" data-testid="cpi-hero-band">
              {current.wording}
            </strong>
          </div>
        </div>

        <p className="clientv2-cpi-lede">{message('C.P.I_indicates')}</p>

        {/* The two readings that get confused with each other: the adviser-level
            indicator above the list, and the per-client bar on every row. Side
            by side and labelled, rather than two paragraphs of prose. */}
        <div className="clientv2-cpi-facts">
          <div className="clientv2-cpi-fact">
            <span className="clientv2-cpi-fact-key">{message('clientv2-client-base')}</span>
            <span>{message('The_top_indicator')}</span>
          </div>
          <div className="clientv2-cpi-fact">
            <span className="clientv2-cpi-fact-key">{message('clientv2-cpi-label')}</span>
            <span>{message('Indicator_attach')}</span>
          </div>
        </div>

        <ul className="clientv2-cpi-legend" data-testid="cpi-legend">
          {bands.map(band => {
            const active = band.level === current.level;
            return (
              <li
                key={band.level}
                data-testid="cpi-legend-row"
                className={active ? 'is-active' : ''}
                style={active ? { backgroundColor: band.tint, borderColor: band.color } : undefined}
              >
                <span className="clientv2-cpi-swatch" style={{ backgroundColor: band.color }} aria-hidden="true" />
                <span>{band.wording}</span>
                {active && <Check size={14} className="clientv2-cpi-tick" aria-hidden="true" />}
              </li>
            );
          })}
        </ul>

        {/* What to do about it, then the caveats — in that order, because the
            first is actionable and the other two are not. */}
        <p className="clientv2-cpi-note">{message('At_least')}</p>
        <p className="clientv2-cpi-fine">
          {message('C.P.I_is_for_reference')}
          <br />
          {message('A.I._engine')}
        </p>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {message('OK')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientBasePopup;
